package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.render.RenderInputs
import dev.lingvo.shared.screenshots.render.ShotRenderer
import dev.lingvo.shared.screenshots.render.Surface
import dev.lingvo.site.components.studio.canvas.CanvasSurface
import kotlin.math.ceil
import kotlin.math.min
import kotlin.math.sqrt

/** Every cached bitmap has explicit ownership and document-space bounds for cheap move transforms. */
internal class StageRaster(val bitmap: dynamic, val bounds: Rect, val bytes: Int) {
    fun close() { bitmap.close() }
}

/** Bounded per-layer rasters include shadows; pointer moves reuse them without fitting or shadow painting. */
internal class StageLayerCache(private val renderer: ShotRenderer, private val maxBytes: Int = 32 * 1024 * 1024) {
    private val entries = linkedMapOf<List<Any?>, StageRaster>()
    val size: Int get() = entries.size
    private var used = 0
    fun layer(state: StudioUiState, layer: Layer, inputs: RenderInputs, scale: Double): StageRaster {
        val key = listOf(layer, state.document.brand, state.document.palette, state.view.locale, state.view.slotId, scale)
        entries.remove(key)?.let { entries[key] = it; return it }
        val page = state.page.copy(layers = state.layers)
        val rawBounds = if (layer is BackgroundLayer) Rect(0.0, 0.0, state.board.canvas.width.toDouble(), state.board.canvas.height.toDouble())
            else editorRasterBounds(page, layer)
        val shadow = layer.base.shadow
        val padding = 2 + (shadow?.blur ?: 0.0) * 3 + maxOf(kotlin.math.abs(shadow?.dx ?: 0.0), kotlin.math.abs(shadow?.dy ?: 0.0))
        val slotOffset = state.page.slotIds.indexOf(state.view.slotId) * state.board.canvas.width.toDouble()
        val bounds = Rect(rawBounds.x - padding - slotOffset, rawBounds.y - padding, rawBounds.w + 2 * padding, rawBounds.h + 2 * padding)
        val rasterScale = min(scale, min(4096 / maxOf(bounds.w, bounds.h), sqrt(maxBytes.toDouble() / 8 / bounds.w / bounds.h)))
        val width = ceil(bounds.w * rasterScale).toInt().coerceAtLeast(1)
        val height = ceil(bounds.h * rasterScale).toInt().coerceAtLeast(1)
        val canvas: dynamic = createStageCanvas(width, height)
        val ctx = canvas.getContext("2d")
        ctx.setTransform(rasterScale, 0, 0, rasterScale, -bounds.x * rasterScale, -bounds.y * rasterScale)
        val adapter = CanvasSurface(ctx, state.view.locale)
        val surface = object : Surface by adapter {
            override val width = state.board.canvas.width
            override val height = state.board.canvas.height
        }
        val pruned = state.document.copy(boards = state.document.boards.map { board ->
            if (board.id != state.board.id) board else board.copy(mirrorRtl = false, pages = board.pages.map {
                if (it.id != state.page.id) it else it.copy(layers = listOf(layer), overrides = emptyMap())
            })
        })
        try { renderer.renderSlot(pruned, state.board.id, state.view.slotId, inputs, surface) }
        catch (failure: Throwable) { canvas.width = 0; canvas.height = 0; throw failure }
        val raster = StageRaster(canvas.transferToImageBitmap(), bounds, width * height * 4)
        canvas.width = 0; canvas.height = 0
        while (entries.isNotEmpty() && used + raster.bytes > maxBytes) {
            val oldest = entries.keys.first(); val removed = entries.remove(oldest)!!
            used -= removed.bytes; removed.close()
        }
        entries[key] = raster; used += raster.bytes
        return raster
    }
    fun clear() { entries.values.forEach { it.close() }; entries.clear(); used = 0 }
}
